import React, { FC, useMemo } from 'react'
import { IColumn } from 'typescript/interfaces/tableUi'
import TableComponent, { TableComponentType } from 'UI/Table'
import { useTableComponentStyle } from 'UI/Table/style'
import { ICustomScheduleResponse } from 'typescript/interfaces/deals'
import { convertDate, convertEmptyField, convertToPriceFormat } from 'utils/formatters'

type CustomScheduledPaymentRow = ICustomScheduleResponse['payments'][number] & {
  paymentFrequency: ICustomScheduleResponse['paymentFrequency']
  bankAccount?: string
}

const TableCustomScheduledPayments = TableComponent as TableComponentType<CustomScheduledPaymentRow>

interface Props {
  customSchedule: ICustomScheduleResponse
}

const CustomScheduledPaymentsTable: FC<Props> = ({ customSchedule }) => {
  const classes = useTableComponentStyle()

  const rows = useMemo<CustomScheduledPaymentRow[]>(
    () =>
      [...(customSchedule.payments ?? [])]
        .sort((a, b) => a.order - b.order)
        .map((p) => ({
          ...p,
          paymentFrequency: customSchedule.paymentFrequency,
          bankAccount: customSchedule.bankAccount,
        })),
    [customSchedule],
  )

  const columns: IColumn<CustomScheduledPaymentRow>[] = useMemo(
    () => [
      {
        label: 'Scheduled Payment Date',
        field: 'scheduledDate',
        bodyCellClassName: classes.accentCell,
        render: (rowData) => convertEmptyField(rowData.scheduledDate ? convertDate(rowData.scheduledDate) : ''),
      },
      {
        label: 'Payment Amount',
        field: 'amount',
        bodyCellClassName: classes.accentCell,
        render: (rowData) => convertToPriceFormat(rowData.amount),
      },
      {
        label: 'Frequency',
        field: 'paymentFrequency',
        bodyCellClassName: classes.accentCell,
        render: (rowData) => convertEmptyField(rowData.paymentFrequency),
      },
      {
        label: 'Bank Account',
        field: 'bankAccount',
        bodyCellClassName: classes.accentCell,
        render: (rowData) => convertEmptyField(rowData.bankAccount),
      },
      {
        label: 'Status',
        field: 'isProcessed',
        bodyCellClassName: classes.accentCell,
        render: (rowData) => (rowData.isProcessed ? 'Processed' : 'Upcoming'),
      },
    ],
    [classes.accentCell],
  )

  return <TableCustomScheduledPayments columns={columns} data={rows} />
}

export default CustomScheduledPaymentsTable
